import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getPublicPrograms,
  getSiteImages,
  PublicProgram,
  SiteImage,
} from "../../lib/publicApi";
import StarField from "../../components/StarField";

export default function Home() {
  const [programs, setPrograms] = useState<PublicProgram[]>([]);
  const [heroImages, setHeroImages] = useState<SiteImage[]>([]);
  const [galleryImages, setGalleryImages] = useState<SiteImage[]>([]);

  useEffect(() => {
    getPublicPrograms().then(setPrograms).catch(() => {});
    getSiteImages("hero").then(setHeroImages).catch(() => {});
    getSiteImages("gallery").then(setGalleryImages).catch(() => {});
  }, []);

  const heroBg = heroImages[0]?.url;

  return (
    <div className="relative">
      {/* ═══ HERO ═══ */}
      <section className="relative flex min-h-[90vh] items-center justify-center overflow-hidden bg-gradient-to-b from-obloha-indigo-950 via-obloha-indigo-900 to-obloha-indigo-950">
        {/* hero fotografie s overlay */}
        {heroBg && (
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url(${heroBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(2px)",
            }}
          />
        )}

        {/* Starfield */}
        <StarField />

        {/* Gradient orbs */}
        <div className="pointer-events-none absolute left-1/4 top-1/3 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-500/20 blur-3xl" />
        <div className="pointer-events-none absolute right-1/4 top-1/2 h-96 w-96 translate-x-1/2 rounded-full bg-obloha-gold-500/15 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/2 h-[400px] w-[800px] -translate-x-1/2 rounded-full bg-obloha-indigo-500/20 blur-3xl" />

        {/* Obsah */}
        <div className="relative z-10 mx-auto max-w-5xl px-6 py-24 text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-sm text-slate-200 backdrop-blur">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400"></span>
            Rezervace na příští měsíce otevřené
          </div>

          <h1 className="bg-gradient-to-b from-white via-white to-obloha-indigo-200 bg-clip-text text-6xl font-black leading-[1.05] tracking-tight text-transparent md:text-8xl">
            Vesmír přímo
            <br />
            <span className="bg-gradient-to-r from-obloha-gold-400 via-obloha-gold-300 to-obloha-gold-500 bg-clip-text text-transparent">
              ve vaší školce
            </span>
          </h1>

          <p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">
            Přivezeme k vám mobilní sférické planetárium a ukážeme dětem hvězdy, planety a kouzlo
            noční oblohy — přímo v tělocvičně nebo herně. Dobrodružství, které jim utkví v paměti.
          </p>

          <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/rezervace"
              className="group relative overflow-hidden rounded-full bg-gradient-to-r from-obloha-gold-500 to-obloha-gold-400 px-10 py-4 text-lg font-semibold text-obloha-indigo-950 shadow-2xl shadow-obloha-gold-500/40 transition-all hover:scale-105 hover:shadow-obloha-gold-400/60"
            >
              <span className="relative z-10">✨ Rezervovat termín</span>
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 group-hover:translate-x-full"></span>
            </Link>
            <Link
              to="/programy"
              className="rounded-full border border-white/30 bg-white/5 px-10 py-4 text-lg font-medium text-white backdrop-blur transition-all hover:bg-white/10"
            >
              Prohlédnout programy →
            </Link>
          </div>

          {/* Mini stats */}
          <div className="mt-16 grid grid-cols-3 gap-4 md:gap-8">
            <Stat number="100%" label="Mobilní" />
            <Stat number="30+" label="Dětí najednou" />
            <Stat number="45 min" label="Program" />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <svg className="h-6 w-6 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </section>

      {/* ═══ JAK TO PROBÍHÁ ═══ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-obloha-indigo-950 to-obloha-indigo-900 py-24">
        <StarField density={0.00004} shootingStars={false} />
        <div className="relative mx-auto max-w-6xl px-6">
          <SectionHeader
            tag="Jak to probíhá"
            title="Kouzlo v 5 krocích"
            subtitle="Od rezervace po ozvěny dětského nadšení"
          />

          <div className="mt-16 grid gap-6 md:grid-cols-3 lg:grid-cols-5">
            {[
              { icon: "📅", title: "Rezervujete", text: "Vyberete termín, programy a počet dětí online" },
              { icon: "✉️", title: "Potvrdíme", text: "Do 24 hodin ozveme se s potvrzením a podrobnostmi" },
              { icon: "🚐", title: "Přijedeme", text: "Postavíme planetárium ve vaší tělocvičně (cca 30 min)" },
              { icon: "🌌", title: "Hrajeme", text: "Děti prožijí pohádkové dobrodružství mezi hvězdami" },
              { icon: "💫", title: "Uklidíme", text: "Vše sbalíme a odjedeme. Vzpomínky zůstanou." },
            ].map((step, i) => (
              <div
                key={i}
                className="group rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-obloha-gold-400/50 hover:bg-white/10"
              >
                <div className="mb-3 text-4xl transition-transform group-hover:scale-110">
                  {step.icon}
                </div>
                <div className="mb-1 text-xs font-bold uppercase text-obloha-gold-400">
                  Krok {i + 1}
                </div>
                <h3 className="mb-2 text-lg font-semibold">{step.title}</h3>
                <p className="text-sm text-slate-300">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PROGRAMY ═══ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-obloha-indigo-900 to-obloha-indigo-950 py-24">
        <div className="pointer-events-none absolute left-0 top-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-purple-500/10 blur-3xl" />
        <div className="pointer-events-none absolute right-0 top-1/3 h-[500px] w-[500px] translate-x-1/2 rounded-full bg-obloha-gold-500/10 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-6">
          <SectionHeader
            tag="Nabídka"
            title="Naše programy"
            subtitle="Každý program jsme vymysleli tak, aby děti vtáhl a nadchl"
          />

          {programs.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-white/10 bg-white/5 p-12 text-center text-slate-300">
              Programy se právě připravují.
            </div>
          ) : (
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {programs.map((p) => (
                <ProgramCard key={p.id} p={p} />
              ))}
            </div>
          )}

          <div className="mt-12 text-center">
            <Link
              to="/programy"
              className="inline-flex items-center gap-2 rounded-full border border-obloha-gold-400/50 bg-obloha-gold-500/10 px-8 py-3 font-semibold text-obloha-gold-300 transition-all hover:bg-obloha-gold-500/20"
            >
              Zobrazit všechny programy →
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ GALERIE ═══ */}
      {galleryImages.length > 0 && (
        <section className="relative overflow-hidden bg-obloha-indigo-950 py-24">
          <div className="mx-auto max-w-7xl px-6">
            <SectionHeader
              tag="Momentky"
              title="Radost v dětských očích"
              subtitle="Fotografie z našich výjezdů do školek"
            />

            <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {galleryImages.slice(0, 8).map((img, i) => (
                <div
                  key={img.id}
                  className={`group relative overflow-hidden rounded-2xl ${
                    i === 0 || i === 5 ? "row-span-2 md:col-span-1" : ""
                  } aspect-square`}
                >
                  <img
                    src={img.url}
                    alt={img.alt ?? "Galerie"}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obloha-indigo-950/90 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  {img.caption && (
                    <div className="absolute bottom-0 left-0 right-0 translate-y-2 p-4 text-sm text-white opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
                      {img.caption}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ CTA ═══ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-obloha-indigo-900 via-purple-900/40 to-obloha-indigo-950 py-24">
        <StarField density={0.00006} />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-4xl font-bold md:text-5xl">
            Připravte dětem den,
            <br />
            <span className="bg-gradient-to-r from-obloha-gold-400 to-obloha-gold-300 bg-clip-text text-transparent">
              na který nezapomenou
            </span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-200">
            Stačí pár kliknutí. Vyberte termín, program a my přijedeme. Bez složitého telefonování,
            bez papírování — jednoduše online.
          </p>
          <div className="mt-10">
            <Link
              to="/rezervace"
              className="inline-block rounded-full bg-gradient-to-r from-obloha-gold-500 to-obloha-gold-400 px-12 py-4 text-lg font-bold text-obloha-indigo-950 shadow-2xl shadow-obloha-gold-500/40 transition-all hover:scale-105"
            >
              ✨ Rezervovat termín teď
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── Podsložky ──────────────────────────────────────────

function SectionHeader({
  tag,
  title,
  subtitle,
}: {
  tag: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="text-center">
      <div className="mb-3 inline-block rounded-full border border-obloha-gold-400/30 bg-obloha-gold-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-obloha-gold-400">
        {tag}
      </div>
      <h2 className="text-4xl font-bold md:text-5xl">{title}</h2>
      {subtitle && <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">{subtitle}</p>}
    </div>
  );
}

function Stat({ number, label }: { number: string; label: string }) {
  return (
    <div>
      <div className="bg-gradient-to-b from-white to-slate-300 bg-clip-text text-3xl font-bold text-transparent md:text-4xl">
        {number}
      </div>
      <div className="mt-1 text-xs uppercase tracking-wider text-slate-400 md:text-sm">{label}</div>
    </div>
  );
}

function ProgramCard({ p }: { p: PublicProgram }) {
  return (
    <Link
      to={`/programy/${p.id}`}
      className="group relative block overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur transition-all hover:-translate-y-1 hover:border-obloha-gold-400/50 hover:shadow-xl hover:shadow-obloha-gold-500/20"
    >
      <div className="aspect-[4/3] w-full overflow-hidden bg-obloha-indigo-800">
        {p.coverImageUrl ? (
          <img
            src={p.coverImageUrl}
            alt={p.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-obloha-indigo-700 to-purple-800 text-7xl">
            ✨
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-xl font-semibold transition-colors group-hover:text-obloha-gold-400">
          {p.name}
        </h3>
        {p.description && (
          <p className="mt-2 text-sm text-slate-300 line-clamp-2">{p.description}</p>
        )}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-wrap gap-2 text-xs text-slate-400">
            <span>⏱ {p.durationMinutes} min</span>
            <span>·</span>
            <span>👥 {p.maxChildrenPerSlot} dětí</span>
          </div>
          <span className="text-sm font-bold text-obloha-gold-400">
            od {p.minPriceCzk.toLocaleString("cs-CZ")} Kč
          </span>
        </div>
      </div>
    </Link>
  );
}
